import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid";
import {
  FetchPaginatedApiModel,
  WithData,
  WithFabObj,
  WithRowOperations,
} from "../../../models/shared/CommonModels";
import { SortingModel } from "../../../models/shared/SortingModel";
import {
  fetchBeniQueryKey,
  fetchBrandBeneSelectQueryKey,
} from "../../../utils/QueryClient";
import { castToBackendSort } from "../../../utils/sorting";
import DeleteDialog from "../../shared/DeleteDialog";
import FilteredDataTable from "../../shared/FilteredDataTable";
import { BeneBrandModel } from "../../../models/api/response/bene/BeneBrandModel";
import { deleteBrand, getBeneBrands } from "../../../api/bene/brand";
import BeneBrandFormComponent from "./BeneBrandFormComponent";

interface BeneBrandTableComponentProps extends WithFabObj {
  enableCreate: boolean;
}

const BeneBrandTableComponent: React.FC<BeneBrandTableComponentProps> = ({
  fabVisible,
  alterFabVisibility,
  enableCreate,
}) => {
  const queryClient = useQueryClient();

  const [editFormOpen, setEditFormOpen] = useState<boolean>(false);
  const toggleEditFormOpen = () => setEditFormOpen(!editFormOpen);

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const toggleDeleteAlert = () => setDeleteAlertOpen(!deleteAlertOpen);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [brands, setBrands] = useState<
    FetchPaginatedApiModel<BeneBrandModel[]>
  >(undefined as any);
  const [selectedRowElement, setSelectedRowElement] =
    useState<BeneBrandModel>();

  const [filters, setFilters] = useState<GridFilterModel | undefined>();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [errorMessage, setErrorMessage] = useState("");
  const hideError = () => setErrorMessage("");

  // query
  useQuery(
    [
      fetchBrandBeneSelectQueryKey,
      paginationModel.page,
      filters,
      sortBy,
      sortDirection,
    ],
    () => {
      const sor = {} as SortingModel;
      sor.field = sortBy || "id";
      sor.direction = sortDirection || "DESC";
      return getBeneBrands(0, paginationModel.page, sor, filters);
    },
    {
      keepPreviousData: true,
      onSuccess: (getResponse) => {
        setBrands(getResponse.data);
      },
      refetchOnWindowFocus: false,
    }
  );

  const deleteMutation = useMutation({
    mutationFn: (x: WithData) => deleteBrand(x.data),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchBeniQueryKey);
    },
    onError: (e: any) => {
      if (e.response.data.message !== undefined)
        setErrorMessage(e.response.data.message);
      else
        setErrorMessage(
          "Errore non previsto, riprovare o richiedere assistenza"
        );
    },
  });

  const editOperations = (
    record: GridRenderCellParams<any, BeneBrandModel>
  ) => {
    setSelectedRowElement(record.value);
    toggleEditFormOpen();
  };

  const deleteOperations = (
    record: GridRenderCellParams<any, BeneBrandModel>
  ) => {
    setSelectedRowElement(record.value);
    toggleDeleteAlert();
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID Brand",
      flex: 1,
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
    },
    {
      field: "descrizione",
      headerName: "Descrizione",
      sortable: false,
      flex: 1,
    },
  ];

  return (
    <>
      <FilteredDataTable<BeneBrandModel, WithRowOperations>
        loading={brands === undefined}
        editOperations={editOperations}
        deleteOperations={deleteOperations}
        rows={
          brands?.data !== undefined
            ? brands.data.map((d) => {
                return {
                  id: d.id,
                  nome: d.nome,
                  descrizione: d.descrizione,
                  actions: d,
                  delete_action: d,
                  hasEdit: true,
                  hasDelete: true,
                };
              })
            : []
        }
        rowCount={brands?.count}
        onPaginationModelChange={setPaginationModel}
        columns={columns}
        errorMessage={errorMessage}
        hideError={hideError}
        filters={filters}
        sortModel={sortModel}
        onFilterChange={(newFilterModel) => setFilters(newFilterModel)}
        onSortChange={(newSortModel) => {
          newSortModel.forEach((item) => {
            if (item !== undefined) {
              setSortBy(item.field);
              setSortDirection(castToBackendSort(item.sort));
            }
          });

          setSortModel(newSortModel);
        }}
      />

      {/* Create */}
      {
        <BeneBrandFormComponent
          initialData={{} as BeneBrandModel}
          open={enableCreate ? !fabVisible : false}
          toggleOpen={() => {}}
          onClose={() => alterFabVisibility(true)}
        />
      }

      {/* Edit */}
      {selectedRowElement !== undefined && (
        <BeneBrandFormComponent
          initialData={selectedRowElement}
          open={editFormOpen}
          toggleOpen={() => {
            toggleEditFormOpen();
          }}
          onClose={() => {
            setSelectedRowElement(undefined);
            toggleEditFormOpen();
          }}
          editEnabled={true}
        />
      )}

      {/* Delete */}
      <DeleteDialog
        open={deleteAlertOpen}
        onClose={toggleDeleteAlert}
        identifier={selectedRowElement?.id}
        onPositive={() => {
          toggleDeleteAlert();
          const x = {} as WithData;
          x.data = selectedRowElement?.id;
          deleteMutation.mutate(x);
        }}
        onNegative={toggleDeleteAlert}
      />
    </>
  );
};

export default BeneBrandTableComponent;
